import {
    Component,
    OnInit,
    Input,
    Output,
    EventEmitter,
    DoCheck,
    HostListener,
    ElementRef,
    OnDestroy,
} from '@angular/core';
import { Router } from '@angular/router';
import { ReusableFunctions } from '../../classes/reusable-functions';
import { DisplayOptionsService } from '../../services/display-options.service';
import {
    DatesToDisplay,
    DisplayOptions,
    DisplayOptionsData,
    DisplayOptionsDdlItems,
    DisplayOptionStorage,
    ICustomDateRangeInfo,
    IDisplayOptionToReset,
    IHistoricalYears,
    IOptionalSummaryList,
    IPageDisplayOption,
    ISummaryAreaInfo,
    ISyncAsOfDateInf,
} from '../../interfaces/display-options';

@Component({
    selector: 'display-options',
    templateUrl: './display-options-menu.component.html',
    styleUrls: ['./display-options-menu.component.scss'],
})
export class DisplayOptionsMenuComponent implements OnInit, DoCheck, OnDestroy {
    isDateRangeApplyBtnDisabled;
    displayOptionsDdlItems: DisplayOptionsDdlItems[];
    displayOptionsStorage: DisplayOptionStorage[];
    displayOptions: DisplayOptions[];
    defaultDisplayOptions: DisplayOptions[];
    displayOptionsUrl: string;
    dashboard: string = '';
    datesToDisplay: DatesToDisplay[];
    resetYearRangeOptionKey: string;
    yearOptions: IHistoricalYears[];
    loading = false;
    showOnlyPreviousDateRange = false;
    summaryAreaItems: ISummaryAreaInfo[];
    extraParameters: IOptionalSummaryList[] = [];
    hasDateRangeSet = false;
    hasDisplayOptionsSet = false;
    isDisplayFilterDialogOpen: boolean;
    datePickerValues: ICustomDateRangeInfo;
    emitDisplayOptionNow: boolean;

    @Output() displayOptionsOutput: EventEmitter<DisplayOptions[]> = new EventEmitter<
        DisplayOptions[]
    >();
    @Output() externalResetOutput?: EventEmitter<DisplayOptions[]> = new EventEmitter<
        DisplayOptions[]
    >();
    @Output() displayDatesOutput?: EventEmitter<DatesToDisplay[]> = new EventEmitter<
        DatesToDisplay[]
    >(); // Is this still using?

    @Input() showEditButton?: boolean;
    @Input() selectSummaryHideList?: DisplayOptions[] = []; // List of display options to hide summary for
    @Input() showCompareToDate?: boolean = true;
    @Input() showRollingDateRange?: boolean = true;
    @Input() optionalSummaryList?: IOptionalSummaryList[] = [];
    @Input('displayOptionsToReset') set displayOptionsToReset(values: IDisplayOptionToReset[]) {
        if (values) {
            this.setDisplayOptionsFromComponent(values);
        }
    }
    @Input('extraParameters') set extraParams(values: IOptionalSummaryList[]) {
        if (values) {
            this.extraParameters = values;
        }
    }
    @Input() syncAsOfDateInf?: ISyncAsOfDateInf;
    @Input() hideDateRangeDates?: boolean = false;
    @Input() hiddenFilterItems?: string[];
    @Input() asOfDateId?: string;

    constructor(
        private _displayOptionsService: DisplayOptionsService,
        private _router: Router,
        private _reusableFunctions: ReusableFunctions,
        private elementRef: ElementRef
    ) {}

    ngOnInit() {
        this.initializeCustomDateRange();
        this.isDisplayFilterDialogOpen = false;
        this.emitDisplayOptionNow = false;
        this.displayOptionsUrl = this._router.url.split('?')[0];
        this.yearOptions = JSON.parse(localStorage.getItem('historical-year-options'));
        this.getDisplayOptionsDdlItems();
    }

    ngDoCheck() {
        if (
            this.extraParameters &&
            this.extraParameters.length > 0 &&
            this.displayOptions &&
            !this.hasDateRangeSet &&
            this.hasDisplayOptionsSet
        ) {
            this.setDateRangeAndDisplayOptionCache();
            this.hasDateRangeSet = true;
        }
    }

    setDisplayOptionsFromComponent(optionListToUpdate: IDisplayOptionToReset[]) {
        for (let value of optionListToUpdate) {
            let optionInfo = this.displayOptionsDdlItems.find(
                (item) => item.optionKey === value.optionKey
            );
            let tobeSelectedOption = optionInfo.filterItems.filterItems.find(
                (item) =>
                    item.friendlyName === value.toBeSetValue ||
                    item.optionName === value.toBeSetValue
            );

            if (!optionInfo.isMultiSelection) {
                optionInfo.filterItems.selectedItems = [tobeSelectedOption];
            } else {
                optionInfo.filterItems.selectedItems = value.toBeSetList;
            }
        }
        this.applyChanges();
        this.externalResetOutput.emit(this.displayOptions);
    }

    showHideSelectedSummary(
        showInpareantPage: boolean,
        showInReportingPage,
        hideSelectSummary: boolean = false
    ): boolean {
        let moduleSegments = this.displayOptionsUrl.split('/');
        let module = moduleSegments[1];

        if (hideSelectSummary) {
            return false;
        }

        if (module === 'reporting') {
            return showInReportingPage;
        }

        return showInpareantPage;
    }

    getDisplayOptionsDdlItems() {
        this.displayOptionsDdlItems = [];
        this.defaultDisplayOptions = [];
        this.summaryAreaItems = [];

        this._displayOptionsService.getPageDisplayOptions(this.displayOptionsUrl).subscribe(
            (res) => {
                res.forEach((item) => {
                    let value = item.displayOptionItems[0];

                    if (value.isUsedForGetDateRange && value.showCustomDateRange) {
                        this.datePickerValues.allowCustomDateRange = true;
                    }

                    let hideSelectSummary = false;
                    if (
                        this.selectSummaryHideList.find(
                            (option) => option.optionKey == value.mainOptionName
                        )
                    ) {
                        hideSelectSummary = true;
                    }
                    this.displayOptionsDdlItems.push({
                        displayOptionKey: value.displayOptionKey,
                        optionKey: value.mainOptionName,
                        filterName: value.mainFriendlyName,
                        filterOrder: value.mainOptionOrder,
                        defaultText: value.mainDefaultFilterText,
                        isMultiSelection: value.isMultiSelection,
                        filteredValue: '',
                        isSelectSummaryShow: this.showHideSelectedSummary(
                            value.isSelectSummaryShow,
                            value.isSelectSummaryShowInDrilldown,
                            hideSelectSummary
                        ),
                        summarySectionTitleName: value.summarySectionTitleName,
                        isUsedForGetDateRange: value.isUsedForGetDateRange,
                        hasDefaultSelection: value.hasDefaultSelection,
                        //getDataFromSproc: value.getDataFromSproc,
                        //sprocDetails: value.sprocDetails,
                        filterItems: {
                            filterItems: [],
                            selectedItems: [],
                            defaultSelection: [],
                        },
                        showCustomDateRange: false,
                    });

                    this.defaultDisplayOptions.push({
                        optionKey: value.mainOptionName,
                        selectedOptions: '',
                        selectedOptionList: [],
                        isMultiSelection: value.isMultiSelection,
                        isSelectSummaryShow: value.isSelectSummaryShow,
                        isSelectSummaryShowInDrilldown: value.isSelectSummaryShowInDrilldown,
                        summarySectionTitleName: value.summarySectionTitleName,
                        dateRangeType: 'standard',
                    });
                });
                // TODO: remove hard coding and pass the value as parameter
                this.displayOptionsDdlItems.map((option) => {
                    if (
                        this.showRollingDateRange === false &&
                        option.optionKey === 'dateRangeRolling'
                    ) {
                        option.hideDisplay = true;
                    }

                    if (
                        this.hiddenFilterItems &&
                        this.hiddenFilterItems.includes(option.optionKey)
                    ) {
                        option.hideFilterItem = true;
                    }
                });
                this.displayOptionsDdlItems.sort((a, b) => a.filterOrder - b.filterOrder);
                this.setFiterDdlItemsSettings();
                this.setDisplayoptionData(res);
            },
            (err) => {
                throw err;
            }
        );
    }

    setAsOfDate() {
        // TODO: remove this if statement after adding syncAsOfDateInf parameter to score cards
        // TODO: Remove asOfDateId from site and get values from syncAsOfDateInf
        if (!this.syncAsOfDateInf) {
            let asOfDateId = this.asOfDateId || 'scorecardasof';
            let syncWithDisplayOptionId = 'daterangerolling';
            let selectedDateRangeOpt = this.displayOptions.find(
                (item) => item.optionKey.toLowerCase() === syncWithDisplayOptionId.toLowerCase()
            );
            let selectedAsOfOpt = this.displayOptions.find(
                (item) => item.optionKey.toLowerCase() === asOfDateId.toLowerCase()
            );
            if (selectedDateRangeOpt && selectedAsOfOpt) {
                this.syncAsOfDateInf = {
                    asOfDateId: asOfDateId,
                    syncWithDisplayOptionId: syncWithDisplayOptionId,
                };
            }
        }

        // TODO: Make sure this run only As of date is in  display options
        if (this.syncAsOfDateInf) {
            let selectedDateRange = this.displayOptions.find(
                (item) =>
                    item.optionKey.toLowerCase() ===
                    this.syncAsOfDateInf.syncWithDisplayOptionId.toLowerCase()
            )?.selectedOptions;

            let selectedAsOf = this.displayOptions.find(
                (item) =>
                    item.optionKey.toLowerCase() === this.syncAsOfDateInf.asOfDateId.toLowerCase()
            )?.selectedOptions;
            const selectedAsOfDate = this.displayOptions.find(
                (item) =>
                    item.optionKey.toLowerCase() === this.syncAsOfDateInf.asOfDateId.toLowerCase()
            )?.selectedOptionsFriendly;
            this._displayOptionsService.getScorecardRanges(selectedDateRange ?? '').subscribe(
                (dates) => {
                    if (dates.length > 0) {
                        let date = dates.find((date) => date.optionIdentifier === selectedAsOf);
                        if (selectedAsOf && selectedDateRange && date) {
                            let fromDate = '';
                            // TODO: check the posibilty to get these values dynamicaly.
                            // Maybe by pivorting the sproc output DisplayScorecardDateList
                            // TODO: Use 'switch' instead of 'if'
                            if (selectedDateRange.includes('12m')) {
                                fromDate = date.from12;
                            } else if (selectedDateRange.includes('6m')) {
                                fromDate = date.from6;
                            } else if (selectedDateRange.includes('3m')) {
                                fromDate = date.from3;
                            } else if (selectedDateRange.includes('36m')) {
                                fromDate = date.from36;
                            } else if (selectedDateRange.includes('24m')) {
                                fromDate = date.from24;
                            } else if (selectedDateRange.includes('48m')) {
                                fromDate = date.from48;
                            } else if (
                                //TODO: Standardize to QTD or This Quarter
                                selectedDateRange.includes('This Quarter') ||
                                selectedDateRange.includes('QTD')
                            ) {
                                fromDate = date.fromQTD;
                            } else if (
                                selectedDateRange.includes('This Year') ||
                                selectedDateRange.includes('YTD')
                            ) {
                                fromDate = date.fromYTD;
                            } else if (selectedDateRange.includes('This Month')) {
                                fromDate = date.fromMTD;
                            } else if (selectedDateRange.includes('Last Month')) {
                                fromDate = date.fromLastMonth;
                            }
                            let toDate = date.optionName;

                            if (fromDate && toDate) {
                                //TODO: Why this if statement??
                                if (this.asOfDateId === 'rankingsasof') toDate = selectedAsOfDate;

                                this.datesToDisplay = [
                                    {
                                        dateKey: 'fromDates',
                                        dateName: 'Date Range',
                                        dateValueFrom: fromDate,
                                        dateValueTo: toDate,
                                    },
                                ];
                                this.addSelectedDateRangeAndSaveToCache();
                            }
                        }
                    }
                },
                (err) => {
                    throw new Error(err);
                }
            );
        }
    }

    setDateRangeAndDisplayOptionCache() {
        // TODO: Stop running daterange logic if date options are not changed
        // probably it will stop unnecessary multiple calls
        this.datesToDisplay = [];
        let fromDateTitleInf = this.extraParameters.find(
            (opt) => opt.title.toLowerCase() === 'fromdate'
        );

        let fromDateTitle = fromDateTitleInf?.value ?? 'Date Range';
        let dateRangeItem = this.displayOptions.find((opt) => opt.isUsedForGetDateRange === true);
        let hasCustomDateRangeSelected =
            this.datePickerValues.allowCustomDateRange &&
            this.datePickerValues.isDateRangeValid &&
            this.datePickerValues.showCustomDateRange;

        if (hasCustomDateRangeSelected) {
            this.datesToDisplay = [
                {
                    dateKey: 'fromDates',
                    dateName: fromDateTitle,
                    dateValueFrom: this.datePickerValues.startDate.toLocaleDateString('es-pa'),
                    dateValueTo: this.datePickerValues.endDate.toLocaleDateString('es-pa'),
                },
            ];
            this.addSelectedDateRangeAndSaveToCache('custom');
        } else {
            this.setAsOfDate();
        }

        if (!this.syncAsOfDateInf && dateRangeItem && !hasCustomDateRangeSelected) {
            let moduleSegments = this.displayOptionsUrl.split('/');
            let _module = moduleSegments[1];
            let kpiModule = ['kpi'];

            if (
                _module === 'performance' &&
                kpiModule.find((mod) => this.displayOptionsUrl.includes(mod))
            ) {
                _module = 'kpi';
            } else if (_module === 'reporting') {
                _module = moduleSegments[2];
                this.showCompareToDate = false; // TODO: This value can pass from the parent component
            }

            let comparedToItem = this.displayOptions.find(
                (opt) => opt.optionKey.toLowerCase() === 'compareto'
            );

            let compareToPeriod = comparedToItem ? comparedToItem.selectedOptions : null;

            if (!compareToPeriod) {
                let extraValue = this.extraParameters.find(
                    (opt) => opt.title.toLowerCase() === 'compareto'
                );
                if (extraValue) {
                    compareToPeriod = extraValue.value;
                }
            }

            let selectedPeriod: string = '';
            let extraPeriod = this.extraParameters.find(
                (opt) => opt.title.toLowerCase() === 'selectedperiod'
            );
            if (extraPeriod) {
                selectedPeriod = extraPeriod.value;
            }

            let showOnlyPreviousDateRangeInfo = this.extraParameters.find(
                (opt) => opt.title.toLowerCase() === 'showonlypreviousdaterange'
            );
            if (showOnlyPreviousDateRangeInfo) {
                this.showOnlyPreviousDateRange =
                    showOnlyPreviousDateRangeInfo.value === 'true' ? true : false;
            }

            this._displayOptionsService
                .getDateRanges(
                    _module,
                    dateRangeItem.selectedOptions,
                    compareToPeriod,
                    selectedPeriod
                )
                .subscribe(
                    (dates) => {
                        if (dates.length > 0) {
                            let fromDate = dates.find(
                                (obj) => obj.tag.toLocaleUpperCase() === 'FROM'
                            );
                            let toDate = dates.find((obj) => obj.tag.toLocaleUpperCase() === 'TO');

                            if (fromDate && toDate && !this.showOnlyPreviousDateRange) {
                                this.datesToDisplay = [
                                    {
                                        dateKey: 'fromDates',
                                        dateName: fromDateTitle,
                                        dateValueFrom: fromDate.dateFullDescription,
                                        dateValueTo: toDate.dateFullDescription,
                                    },
                                ];
                            }

                            let compareFrom = dates.find(
                                (obj) => obj.tag.toLocaleUpperCase() === 'COMPARE FROM'
                            );
                            let compareTo = dates.find(
                                (obj) => obj.tag.toLocaleUpperCase() === 'COMPARE TO'
                            );

                            if (compareFrom && compareTo) {
                                if (this.showCompareToDate && !this.showOnlyPreviousDateRange) {
                                    this.datesToDisplay.push({
                                        dateKey: 'compareDates',
                                        dateName: 'Compared To',
                                        dateValueFrom: compareFrom.dateFullDescription,
                                        dateValueTo: compareTo.dateFullDescription,
                                    });
                                }

                                if (this.showOnlyPreviousDateRange) {
                                    this.datesToDisplay = [];
                                    this.datesToDisplay.push({
                                        dateKey: 'previousPeriod',
                                        dateName: fromDateTitle,
                                        dateValueFrom: compareFrom.dateFullDescription,
                                        dateValueTo: compareTo.dateFullDescription,
                                    });
                                }
                            }
                        }
                        this.addSelectedDateRangeAndSaveToCache();
                    },
                    (err) => {
                        throw new Error(err);
                    }
                );
        } else if (!hasCustomDateRangeSelected) {
            this.addSelectedDateRangeAndSaveToCache();
        }
    }

    addSelectedDateRangeAndSaveToCache(dateRangeType: 'custom' | 'standard' = 'standard') {
        let optionWithDates = this.displayOptions.find((opt) => opt.isUsedForGetDateRange);
        if (optionWithDates) {
            optionWithDates.dateRange = this.datesToDisplay;
            optionWithDates.dateRangeType = dateRangeType;

            if (dateRangeType === 'custom') {
                this.datePickerValues.hasApplied = true;
                this.datePickerValues.valueChanged = false;
            }
        }

        if (this.displayDatesOutput) {
            this.displayDatesOutput.emit(this.datesToDisplay); // Emit dates displayed in From Date
        }
        this.setDisplayOptionsStorage();
    }

    setDisplayOptionsStorage() {
        this.displayOptionsStorage = JSON.parse(localStorage.getItem('displayOptionsStorage'));
        let appVersion = localStorage.getItem('version');

        if (this.displayOptionsStorage) {
            let currentOption = this.displayOptionsStorage.find(
                (option) => option.optionName === this.displayOptionsUrl
            );

            if (currentOption) {
                currentOption.optionValues = this.displayOptions;
            } else {
                if (this.displayOptions) {
                    this.displayOptionsStorage.push({
                        optionName: this.displayOptionsUrl,
                        optionValues: this.displayOptions,
                        version: appVersion,
                    });
                }
            }
        } else {
            if (this.displayOptions) {
                this.displayOptionsStorage = [
                    {
                        optionName: this.displayOptionsUrl,
                        optionValues: this.displayOptions,
                        version: appVersion,
                    },
                ];
            }
        }
        this.checkForEditButtonVisibility();
        this.syncCommonDisplayOptionsAndUpdateCache();
    }

    checkForEditButtonVisibility() {
        if (this.showEditButton === undefined || this.showEditButton === null) {
            let isMultipleOptions = false;
            this.displayOptionsDdlItems.forEach((opt) => {
                if (opt.filterItems.filterItems.length > 1) {
                    isMultipleOptions = true;
                }
            });
            this.showEditButton = isMultipleOptions;
        }
    }

    setFiterDdlItemsSettings() {
        let defaultDdlSettings = {
            singleSelection: true,
            text: '',
            selectAllText: '',
            unSelectAllText: '',
            enableSearchFilter: false,
            classes: '',
            badgeShowLimit: 3,
            enableCheckAll: false,
            showCheckbox: false,
            labelKey: 'friendlyName',
            primaryKey: 'optionOrder',
            enableFilterSelectAll: false,
        };

        this.displayOptionsDdlItems.map((item, index) => {
            if (item.optionKey === 'yearsDdl') {
                this.resetYearRangeOptionKey = item.optionKey;
            }

            let ddlSettings = Object.assign({}, defaultDdlSettings);

            if (item.isMultiSelection === true) {
                ddlSettings.singleSelection = false;
                ddlSettings.showCheckbox = true;
            } else {
                ddlSettings.singleSelection = true;
                ddlSettings.showCheckbox = false;
            }
            ddlSettings.text =
                item.isMultiSelection && !item.hasDefaultSelection ? 'All' : item.defaultText;
            item.filterItems.settings = ddlSettings;
        });
    }

    fetchMore(event: any, ddl: DisplayOptionsDdlItems) {
        if (ddl.getDataFromSproc) {
            if (event.end === ddl.filterItems.filterItems.length - 1) {
                //this.loading = true;
                //this.appService.getChunkData(this.itemList.length, this.bufferSize).then(chunk => {
                //    this.itemList = this.itemList.concat(chunk);
                //    this.loading = false;
                //}, () => this.loading = false);
            }
        }
    }

    getCurrentDisplayOptionsIfAny(
        defaultDisplayOptions: DisplayOptions[],
        optionName?: string
    ): DisplayOptions[] {
        let displayOptionsLocalStorage: DisplayOptionStorage[] = JSON.parse(
            localStorage.getItem('displayOptionsStorage')
        );
        let defltOpt = this._reusableFunctions.getCurrentDisplayOptions(this.displayOptionsUrl);

        let displayOptionsInfo: DisplayOptionStorage;
        if (displayOptionsLocalStorage) {
            displayOptionsInfo = displayOptionsLocalStorage.find(
                (option) => option.optionName === optionName
            );

            if (displayOptionsInfo) {
                if (displayOptionsInfo.optionValues) {
                    return displayOptionsInfo.optionValues;
                }
            } else {
                if (defltOpt) {
                    return defltOpt;
                }
            }
        } else if (defltOpt) {
            return defltOpt;
        }
        return defaultDisplayOptions;
    }

    setDisplayoptionData(pageDisplayOptions: IPageDisplayOption[]) {
        pageDisplayOptions.forEach((pageOption) => {
            let res = pageOption.displayOptionItems;
            if (res && res.length > 0) {
                if (res[0].mainOptionName === this.resetYearRangeOptionKey) {
                    res.forEach((data) => {
                        let yearData = this.yearOptions.find(
                            (itm) => itm.yearId === data.optionName
                        );
                        data.friendlyName = yearData.year;
                        data.summaryDisplayName = yearData.year;
                    });
                }

                let defaultSele = res.find((opt) => opt.isDefaultSelection === true);
                let option = this.displayOptionsDdlItems.find(
                    (opt) => opt.optionKey === res[0].mainOptionName
                );
                if (option) {
                    let onlyRequiredOptions: DisplayOptionsData[] = [];
                    res.forEach((itm) => {
                        onlyRequiredOptions.push({
                            displayOptionKey: itm.displayOptionKey,
                            mainOptionName: itm.mainOptionName,
                            optionOrder: itm.optionOrder,
                            optionName: itm.optionName,
                            friendlyName: itm.friendlyName,
                            isDefaultSelection: itm.isDefaultSelection,
                            summaryDisplayName: itm.summaryDisplayName,
                        });
                    });
                    option.filterItems.filterItems = onlyRequiredOptions;

                    let defaultOpt = this.defaultDisplayOptions.find(
                        (opt) => opt.optionKey === option.optionKey
                    );

                    if (option.isMultiSelection) {
                        defaultOpt.selectedOptions = defaultSele ? defaultSele.friendlyName : 'All';
                        defaultOpt.selectedOptionsFriendly = defaultSele
                            ? defaultSele.summaryDisplayName
                            : 'All';
                        defaultOpt.isUsedForGetDateRange = option.isUsedForGetDateRange;

                        let selectedOptions = [];
                        if (option.optionKey === this.resetYearRangeOptionKey) {
                            for (let yearInf of this.yearOptions) {
                                if (yearInf.yearInfo.visible) {
                                    selectedOptions.push(
                                        res.find((item) => item.optionName === yearInf.yearId)
                                    );
                                }
                            }
                        } else {
                            selectedOptions = defaultSele ? [Object.assign({}, defaultSele)] : [];
                        }
                        option.filterItems.selectedItems = selectedOptions;
                        defaultOpt.selectedOptionList = selectedOptions;
                        option.filterItems.defaultSelection = selectedOptions;
                    } else {
                        defaultOpt.selectedOptions = defaultSele ? defaultSele.optionName : '';
                        defaultOpt.selectedOptionsFriendly = defaultSele
                            ? defaultSele.summaryDisplayName
                            : '';
                        defaultOpt.selectedOptionList = [Object.assign({}, defaultSele)];
                        defaultOpt.isUsedForGetDateRange = option.isUsedForGetDateRange;
                        option.filterItems.defaultSelection = [Object.assign({}, defaultSele)];
                    }
                }
            }
        });

        this.displayOptions = this.getCurrentDisplayOptionsIfAny(
            this.defaultDisplayOptions,
            this.displayOptionsUrl
        );

        for (let opt of this.displayOptions) {
            if (opt.optionKey === this.resetYearRangeOptionKey) {
                let freindlyValue = '';
                opt.selectedOptionList.sort((a, b) =>
                    this._reusableFunctions.sortAlphaNumeric(
                        a.optionOrder.toString(),
                        b.optionOrder.toString()
                    )
                );
                opt.selectedOptionList.forEach((data) => {
                    let yearData = this.yearOptions.find((itm) => itm.yearId === data.optionName);
                    data.friendlyName = yearData.year;
                    data.summaryDisplayName = yearData.year;
                    freindlyValue += yearData.year + ', ';
                });

                freindlyValue = freindlyValue.slice(0, freindlyValue.length - 2);
                opt.selectedOptions = freindlyValue;
                opt.selectedOptionsFriendly = freindlyValue;
            }
        }
        this.hasDisplayOptionsSet = true;
        this.updateDisplayOptionsDdlItems();
        this.setDateRangeAndDisplayOptionCache();
    }

    updateDisplayOptionsDdlItems(
        opt: DisplayOptions = null,
        optionDdlItem: DisplayOptionsDdlItems = null
    ) {
        if (opt && optionDdlItem) {
            optionDdlItem.filterItems.defaultSelection = opt.selectedOptionList.map((obj) => ({
                ...obj,
            }));
            optionDdlItem.filterItems.selectedItems = opt.selectedOptionList.map((obj) => ({
                ...obj,
            }));
            optionDdlItem.filteredValue = opt.selectedOptionsFriendly;
        } else {
            for (let opt of this.displayOptions) {
                let option = this.displayOptionsDdlItems.find(
                    (item) => item.optionKey === opt.optionKey
                );
                if (option) {
                    option.filterItems.defaultSelection = opt.selectedOptionList.map((obj) => ({
                        ...obj,
                    }));
                    option.filterItems.selectedItems = opt.selectedOptionList.map((obj) => ({
                        ...obj,
                    }));
                    option.filteredValue = opt.selectedOptionsFriendly;
                }
            }
        }
    }

    updateDisplayoption(option: DisplayOptionsDdlItems, displayOptions: DisplayOptions[]) {
        let value = '';
        let freindlyValue = '';
        let multiValues: DisplayOptionsData[] = [];
        let defaultSelection: DisplayOptionsData;

        multiValues = option.filterItems.selectedItems.map((obj) => ({
            ...obj,
        }));
        if (option.isMultiSelection === true) {
            defaultSelection = option.filterItems.filterItems.find(
                (item) => item.isDefaultSelection === true
            );

            if (multiValues.length === 0 && defaultSelection) {
                if (defaultSelection.optionName === this.resetYearRangeOptionKey) {
                    option.filterItems.selectedItems = displayOptions.find(
                        (item) => item.optionKey === this.resetYearRangeOptionKey
                    ).selectedOptionList;
                    multiValues = option.filterItems.selectedItems.map((obj) => ({ ...obj }));
                } else {
                    multiValues = [Object.assign({}, defaultSelection)];
                }
            }

            if (multiValues.length === 0) {
                value = 'All';
                freindlyValue = value;
            } else {
                multiValues.sort((a, b) =>
                    this._reusableFunctions.sortAlphaNumeric(
                        a.optionOrder.toString(),
                        b.optionOrder.toString()
                    )
                );
                for (let item of multiValues) {
                    value += item.optionName + ', ';
                    freindlyValue += item.summaryDisplayName + ', ';
                }
                value = value.slice(0, value.length - 2);
                freindlyValue = freindlyValue.slice(0, freindlyValue.length - 2);
            }
        } else {
            if (multiValues.length > 0) {
                value = multiValues[0].optionName;
                freindlyValue = multiValues[0].summaryDisplayName;
            }
        }
        let dspOpt = displayOptions.find((item) => item.optionKey === option.optionKey);
        dspOpt.selectedOptions = value;
        dspOpt.selectedOptionList = multiValues.map((obj) => ({ ...obj }));
        dspOpt.selectedOptionsFriendly = freindlyValue;
    }

    setNewDisplayOptions() {
        this.setCustomDateRangeStatus();
        for (let option of this.displayOptionsDdlItems) {
            let dspOpt = this.displayOptions.find((opt) => opt.optionKey === option.optionKey);

            this.updateDisplayoption(option, this.displayOptions);
            this.updateDisplayOptionsDdlItems(dspOpt, option);
        }
    }

    // todo: Check if there is an unnecessary api call here
    resetDependingDisplayoptions(event: DisplayOptionsData) {
        let eventDetails = this.displayOptionsDdlItems.find(
            (item) => item.optionKey === event.mainOptionName
        );
        let dependingOptions = this.displayOptionsDdlItems.filter(
            (item) => item.getFromOptionKey === eventDetails.optionKey
        );

        dependingOptions.forEach((item, idx) => {
            if (item.getFromDynamicDisplayOption || item.getFromStaticDisplayOption) {
                this.dependingOptionsHttpCall(item).subscribe((result) => {
                    item.filterItems.filterItems = result;
                    if (result.length > 0) {
                        item.hideDdlController = false;
                        let selectedItms = this.checkAndGetSelectedDisplayoptionsInMainDdlList(
                            item.filterItems.selectedItems,
                            result
                        );
                        if (selectedItms.length > 0) {
                            item.filterItems.selectedItems = selectedItms;
                        } else {
                            let defSel = [result.find((i) => i.isDefaultSelection === true)];
                            item.filterItems.selectedItems = defSel;
                        }
                    } else {
                        item.filterItems.selectedItems = [];
                        item.hideDdlController = true;
                    }
                });
            }
        });
    }

    dependingOptionsHttpCall(dspOptDdlItms: DisplayOptionsDdlItems) {
        let displayOptionsCopy = this.displayOptions.map((obj) => ({ ...obj }));
        let getFromItem = this.displayOptionsDdlItems.find(
            (itm) => itm.optionKey === dspOptDdlItms.getFromOptionKey
        );
        this.updateDisplayoption(getFromItem, displayOptionsCopy);
        let potentialGetFromOption = displayOptionsCopy.find(
            (opt) => opt.optionKey === dspOptDdlItms.getFromOptionKey
        );
        let paramInfo = dspOptDdlItms.sprocDetailsObject.sprocParameters.find(
            (prm) => prm.getFromDynamicDisplayOption || prm.getFromStaticDisplayOption
        );

        if (paramInfo) {
            paramInfo.value = potentialGetFromOption.selectedOptions;
        }

        return this._displayOptionsService.getDisplayoptionsFromGivenSproc(
            JSON.stringify(dspOptDdlItms.sprocDetailsObject),
            dspOptDdlItms.displayOptionKey,
            dspOptDdlItms.hasDefaultSelection
        );
    }

    // todo: optimize this
    resetDependingDisplayoptionsDdlList() {
        this.displayOptionsDdlItems.forEach((item) => {
            if (item.getFromDynamicDisplayOption || item.getFromStaticDisplayOption) {
                this.dependingOptionsHttpCall(item).subscribe((result) => {
                    if (result.length > 0) {
                        item.filterItems.filterItems = result;
                    } else {
                        item.filterItems.selectedItems = [];
                        item.hideDdlController = true;
                    }
                });
            }
        });
    }

    checkAndGetSelectedDisplayoptionsInMainDdlList(
        selectedOptions: DisplayOptionsData[],
        mainDdlList: DisplayOptionsData[]
    ) {
        let selectedItms: DisplayOptionsData[] = [];
        for (let itm of selectedOptions) {
            let res = mainDdlList.find((a) => a.optionName === itm.friendlyName);
            if (res) {
                selectedItms.push(itm);
            }
        }
        return selectedItms;
    }

    applyChanges() {
        this.setNewDisplayOptions();
        this.setDateRangeAndDisplayOptionCache();
    }

    emitDisplayOptions() {
        this.emitDisplayOptionNow = true;
        this.applyChanges();
    }

    setButtonStatus() {
        this.isDateRangeApplyBtnDisabled = true;
        let isDdlsEmpty = false;
        let isSelectionDiffrent = false;
        for (let option of this.displayOptionsDdlItems) {
            if (option.filterItems.filterItems.length > 0) {
                if (option.filterItems.selectedItems) {
                    for (let item of option.filterItems.selectedItems) {
                        let defaultItem = option.filterItems.defaultSelection.find(
                            (itm) => item.optionName === itm.optionName
                        );

                        if (!defaultItem) {
                            isSelectionDiffrent = true;
                        }
                    }
                }

                if (
                    option.filterItems.selectedItems.length !==
                    option.filterItems.defaultSelection.length
                ) {
                    isSelectionDiffrent = true;
                }

                if (!option.isMultiSelection && option.filterItems.selectedItems.length === 0) {
                    isDdlsEmpty = true;
                }

                if (
                    option.hasDefaultSelection &&
                    option.isMultiSelection &&
                    option.filterItems.selectedItems.length === 0
                ) {
                    isDdlsEmpty = true;
                }

                // TODO: check the logic and adjust accordingly
                if (
                    option.showCustomDateRange &&
                    this.datePickerValues.isDateRangeValid &&
                    !this.datePickerValues.hasApplied &&
                    this.datePickerValues.valueChanged
                ) {
                    isSelectionDiffrent = true;
                }

                if (
                    this.datePickerValues.hasApplied &&
                    this.datePickerValues.dateRangeType === 'standard'
                ) {
                    isSelectionDiffrent = true;
                }

                if (!isDdlsEmpty && isSelectionDiffrent) {
                    this.isDateRangeApplyBtnDisabled = false;
                } else {
                    this.isDateRangeApplyBtnDisabled = true;
                }
            }
        }

        if (
            this.datePickerValues.showCustomDateRange &&
            this.datePickerValues.allowCustomDateRange &&
            !this.datePickerValues.isDateRangeValid
        ) {
            this.isDateRangeApplyBtnDisabled = true;
        }
    }

    onItemSelect(event: DisplayOptionsData) {
        this.resetDependingDisplayoptions(event);
        this.setButtonStatus();
    }

    onItemDeSelect(event: DisplayOptionsData) {
        let clickedOption = this.displayOptionsDdlItems.find(
            (item) => item.displayOptionKey === event.displayOptionKey
        );
        if (clickedOption) {
            if (!clickedOption.isMultiSelection) {
                clickedOption.filterItems.selectedItems = [
                    clickedOption.filterItems.filterItems.find(
                        (item) => item.optionName === event.optionName
                    ),
                ];
            }
        }

        this.resetDependingDisplayoptions(event);
        this.setButtonStatus();
    }

    onSelectAll(event) {}
    onDeSelectAll(event) {}

    clickEditButton() {
        this.updateDisplayOptionsDdlItems();
        this.resetDependingDisplayoptionsDdlList();
        this.setButtonStatus();
    }

    setContainerHeight(
        elementId: string,
        isOpen: boolean,
        arrayLength: number,
        isSearchBox = false
    ) {
        let ddlArea = document.getElementById(elementId);
        if (ddlArea) {
            if (isOpen) {
                let heightSearchBox = isSearchBox ? 38 : 0;
                let height1ListItem = 38;
                let heightMoreThan6Items = heightSearchBox + 6 * height1ListItem;
                let heightLessthan6Items = heightSearchBox + arrayLength * height1ListItem;
                let heightNoItems = 60 + heightSearchBox;

                ddlArea.style.paddingBottom =
                    (arrayLength === 0
                        ? heightNoItems
                        : arrayLength > 5
                        ? heightMoreThan6Items
                        : heightLessthan6Items) + 'px';
            } else {
                setTimeout(() => {
                    ddlArea.style.paddingBottom = '0px';
                }, 100);
            }
        }
    }

    syncCommonDisplayOptionsAndUpdateCache() {
        // TODO: To sync date rages similar filter items, get a list of date rage options
        // and match the parent option name. Then override what is already being in default
        let defaultDisplayOptionStorage: DisplayOptionStorage[] = JSON.parse(
            localStorage.getItem('tempDefaultDisplayOptionStorage')
        );
        let dilldownUrlsToSync = this._reusableFunctions.getUniqueItemList(
            defaultDisplayOptionStorage,
            'optionName'
        );

        this.displayOptions.forEach((dspOption) => {
            dilldownUrlsToSync.forEach((urlDetail, index) => {
                let displayOptionItems = this.displayOptionsStorage.find(
                    (item) => item.optionName.toLowerCase() === urlDetail.toLowerCase()
                );
                if (displayOptionItems && this.displayOptionsUrl != urlDetail) {
                    if (displayOptionItems.optionValues) {
                        let valueToChange = displayOptionItems.optionValues.find(
                            (item) => item.optionKey === dspOption.optionKey
                        );

                        if (valueToChange) {
                            valueToChange.isMultiSelection = dspOption.isMultiSelection;
                            valueToChange.isUsedForGetDateRange = dspOption.isUsedForGetDateRange;
                            valueToChange.optionKey = dspOption.optionKey;
                            valueToChange.selectedOptionList = dspOption.selectedOptionList;
                            valueToChange.selectedOptions = dspOption.selectedOptions;
                            valueToChange.selectedOptionsFriendly =
                                dspOption.selectedOptionsFriendly;
                        }
                    }
                }

                let defaultDisplayOptionItems = defaultDisplayOptionStorage.find(
                    (item) => item.optionName.toLowerCase() === urlDetail.toLowerCase()
                );

                if (defaultDisplayOptionItems) {
                    if (defaultDisplayOptionItems.optionValues) {
                        let defaultValueToChange = defaultDisplayOptionItems.optionValues.find(
                            (item) => item.optionKey === dspOption.optionKey
                        );
                        if (defaultValueToChange) {
                            defaultValueToChange.isMultiSelection = dspOption.isMultiSelection;
                            defaultValueToChange.isUsedForGetDateRange =
                                dspOption.isUsedForGetDateRange;
                            defaultValueToChange.optionKey = dspOption.optionKey;
                            defaultValueToChange.selectedOptionList = dspOption.selectedOptionList;
                            defaultValueToChange.selectedOptions = dspOption.selectedOptions;
                            defaultValueToChange.selectedOptionsFriendly =
                                dspOption.selectedOptionsFriendly;
                        }
                    }
                }
            });
        });

        localStorage.setItem('displayOptionsStorage', JSON.stringify(this.displayOptionsStorage));
        localStorage.setItem(
            'tempDefaultDisplayOptionStorage',
            JSON.stringify(defaultDisplayOptionStorage)
        );

        if (this.emitDisplayOptionNow) {
            this.displayOptionsOutput.emit(this.displayOptions);
            this.emitDisplayOptionNow = false;
        }
    }

    datePickerStatus(opened: boolean) {
        this.datePickerValues.isDatePickerOpened = opened;
    }

    setInitialCustomDateRangeStatusOnCache(dateRangeType: 'standard' | 'custom') {
        this.displayOptionsStorage = JSON.parse(localStorage.getItem('displayOptionsStorage'));

        if (this.displayOptionsStorage) {
            let currentOption = this.displayOptionsStorage.find(
                (option) => option.optionName === this.displayOptionsUrl
            );

            if (currentOption && currentOption.optionValues) {
                let currentDisplayOptions = currentOption.optionValues;
                let optionWithDates = currentDisplayOptions.find(
                    (opt) => opt.isUsedForGetDateRange
                );
                if (optionWithDates) {
                    optionWithDates.dateRange = [];
                    optionWithDates.dateRangeType = dateRangeType;
                }
            }
            localStorage.setItem(
                'displayOptionsStorage',
                JSON.stringify(this.displayOptionsStorage)
            );
        }
    }

    showCustomDateRangeSelection(
        dateRangeType: 'standard' | 'custom',
        ddl: DisplayOptionsDdlItems
    ) {
        ddl.showCustomDateRange = dateRangeType === 'custom';
        this.datePickerValues.dateRangeType = dateRangeType;
        this.datePickerValues.showCustomDateRange = ddl.showCustomDateRange;
        if (dateRangeType === 'custom') {
            this.setContainerHeight('sub-filter-' + ddl.optionKey, false, 0);
        }
        this.setInitialCustomDateRangeStatusOnCache(dateRangeType);
        this.setButtonStatus();
    }

    resetDateRangeDates() {
        this.datePickerValues.startDate = null;
        this.datePickerValues.endDate = null;
        this.datePickerValues.dateRangeErrorMessage = '';
        this.datePickerValues.isStartDateValid = false;
        this.datePickerValues.isEndDateValid = false;
        this.datePickerValues.isDateRangeValid = false;
        this.datePickerValues.hasApplied = false;
    }

    setCustomDateRangeStatus() {
        if (this.datePickerValues.dateRangeType === 'standard') {
            this.resetDateRangeDates();
        }
    }

    isDateDiffrent(date1: Date, date2: Date) {
        return date1 !== date2;
    }

    onCustomDateRangeChange(event, dateId: 'startDate' | 'endDate') {
        this.datePickerValues.isDateRangeValid = false;
        this.datePickerValues.dateRangeErrorMessage = '';

        if (dateId === 'startDate') {
            this.datePickerValues.minDate = this.setCustomDateRangeMinDate(event.value);
            this.datePickerValues.valueChanged = this.isDateDiffrent(
                this.datePickerValues.startDate,
                event.value
            );
            this.datePickerValues.startDate = event.value;
            this.datePickerValues.isStartDateValid =
                this.datePickerValues.minDate.setHours(0, 0, 0, 0) <=
                    this.datePickerValues.startDate.setHours(0, 0, 0, 0) &&
                this.datePickerValues.startDate !== null;
        } else if (dateId === 'endDate') {
            this.datePickerValues.maxDate = this.setCustomDateRangeMaxDate(event.value);
            this.datePickerValues.valueChanged = this.isDateDiffrent(
                this.datePickerValues.endDate,
                event.value
            );
            this.datePickerValues.endDate = event.value;
            this.datePickerValues.isEndDateValid =
                this.datePickerValues.maxDate >= this.datePickerValues.endDate &&
                this.datePickerValues.endDate !== null;
        }

        if (this.datePickerValues.isEndDateValid && this.datePickerValues.isStartDateValid) {
            this.datePickerValues.isDateRangeValid = true;
        }

        if (this.datePickerValues.startDate === null) {
            this.datePickerValues.dateRangeErrorMessage = 'Enter a valid start date';
        }

        if (this.datePickerValues.endDate === null) {
            this.datePickerValues.dateRangeErrorMessage = 'Enter a valid end date';
        }

        if (
            !this.datePickerValues.isDateRangeValid &&
            this.datePickerValues.minDate !== null &&
            this.datePickerValues.maxDate !== null
        ) {
            this.datePickerValues.dateRangeErrorMessage =
                'The range must be between ' +
                this.datePickerValues.minDate.toLocaleDateString('es-pa') +
                ' and ' +
                this.datePickerValues.maxDate.toLocaleDateString('es-pa');
        }

        if (this.datePickerValues.valueChanged) {
            this.datePickerValues.hasApplied = false;
        }
        this.setButtonStatus();
    }

    initializeCustomDateRange() {
        this.datePickerValues = {
            startDate: null,
            endDate: null,
            showCustomDateRange: false,
            allowCustomDateRange: false,
            minDate: null,
            maxDate: null,
            isEndDateValid: false,
            isStartDateValid: false,
            isDateRangeValid: false,
            isDatePickerOpened: false,
            dateRangeErrorMessage: '',
            hasApplied: false,
            valueChanged: false,
            dateRangeType: 'standard',
        };
        this.setInitialCustomDateRangeStatusOnCache(this.datePickerValues.dateRangeType);
    }

    setCustomDateRangeMinDate(date: Date) {
        return date;
    }

    setCustomDateRangeMaxDate(date: Date) {
        if (this.datePickerValues.minDate === null && this.datePickerValues.maxDate === null) {
            return date;
        } else if (this.datePickerValues.minDate !== null) {
            let minDate = new Date(this.datePickerValues.minDate);
            return new Date(minDate.setFullYear(minDate.getFullYear() + 1));
        }
    }

    setCustomDateRangeStartDate() {
        let today = new Date();
        return new Date(today.setMonth(today.getMonth() - 1));
    }

    @HostListener('document:mousedown', ['$event'])
    onClick(event: MouseEvent) {
        if (
            !this.elementRef.nativeElement.contains(event.target) &&
            !this.datePickerValues.isDatePickerOpened
        ) {
            this.isDisplayFilterDialogOpen = false;
        }
    }

    ngOnDestroy() {
        this.setInitialCustomDateRangeStatusOnCache('standard');
    }
}
